import React, { memo, Fragment, useState, useMemo } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Icon from '../Icon';
import InfoSection from '../Paperwork/components/InfoSection';
import InsuranceCardsSection from './components/InsuranceCardsSection';
import UserDocumentsPreview from '../ProfileLayout/InsuranceDetails/UserDocumentsPreview';

import { COLORS } from '../../constants/styles';
import { ERROR_CODES } from '../../constants/error-codes';
import { DEFAULT_EMPTY_VALUE } from '../../constants/strings';

import { checkIsPaperworkFlow, hasPrimarySubscriberData } from './utils';
import {
  getAdditionalInsuranceCardDocumentsConfig,
  getInsuranceCardDocumentsConfig,
} from '../../utils/getDocumentsConfig';

import useInsuranceInfo from './hooks/useInsuranceInfo';
import useInsuranceCards from '../../hooks/patientInsurance/useInsuranceCards';
import useGetPatientMainInsuranceInfo from './hooks/useGetPatientMainInsuranceInfo';
import useGetHaveInsuranceLabel from '../../hooks/patientInsurance/useGetHaveInsuranceLabel';
import useIsAdditionalInsurancesEnabledAndMaxNumber from '../../hooks/patientInsurance/useIsAdditionalInsurancesEnabledAndMaxNumber';

import { selectInsuranceLatestCheckError } from '../../redux/appointmentForm/selectors';
import { selectPatientInsurancesList } from '../../redux/manage-insurances/selectors/get-patient-insurances-list';

import styles from './index.styl';
import { selectPaperworkIsPlanNotListedSelected } from '../../redux/paperwork/selectors/common';

const ICON_STYLES = { fill: COLORS.bluePrimary };

const InsuranceSection = memo(
  ({
    insurance,
    flowType,
    isAdditionalInsurance,
    additionalInsuranceKey,
    skipInsurance,
  }) => {
    const { formatMessage } = useIntl();

    const { frontSide, backSide } = insurance;

    const insuranceCardsHookArgs = useMemo(() => {
      return {
        frontSide,
        backSide,
        frontSideKey: isAdditionalInsurance
          ? `${additionalInsuranceKey}_1`
          : undefined,
        backSideKey: isAdditionalInsurance
          ? `${additionalInsuranceKey}_2`
          : undefined,
      };
    }, [frontSide, backSide, isAdditionalInsurance, additionalInsuranceKey]);

    const insuranceCards = useInsuranceCards(insuranceCardsHookArgs);

    const { sectionTitle, fields } = useMemo(() => {
      if (isAdditionalInsurance) {
        return getAdditionalInsuranceCardDocumentsConfig({
          formatMessage,
          userDocuments: insuranceCards,
          isEditMode: false,
          fieldName: additionalInsuranceKey,
        });
      }

      return getInsuranceCardDocumentsConfig({
        formatMessage,
        userDocuments: insuranceCards,
        isEditMode: false,
      });
    }, [insuranceCards, additionalInsuranceKey, isAdditionalInsurance]);

    const filteredDocuments = insuranceCards.filter(
      doc => doc.attachment && doc.attachment.url,
    );

    const { primarySubscriber } = insurance;

    const showPrimarySubscriberSection = hasPrimarySubscriberData(
      primarySubscriber,
    );

    const primarySubscriberTitle = formatMessage({
      id: 'fields.insurance.primarySubscriber.title',
    });

    const {
      getPayerAndVerificationDate,
      getMemberIdAndGroupNumber,
      getPrimarySubscriberAddressData,
      getPrimarySubscriberMainData,
    } = useInsuranceInfo();

    return (
      <Fragment>
        <InsuranceCardsSection
          fields={fields}
          flowType={flowType}
          isAdditionalInsurance={isAdditionalInsurance}
          skipInsurance={skipInsurance}
          title={sectionTitle}
        />
        <InfoSection
          entries={getPayerAndVerificationDate(
            insurance,
            flowType,
            isAdditionalInsurance,
            skipInsurance,
          )}
        />
        <InfoSection
          entries={getMemberIdAndGroupNumber(
            insurance,
            flowType,
            isAdditionalInsurance,
            skipInsurance,
          )}
        />

        {!skipInsurance && showPrimarySubscriberSection && (
          <Fragment>
            <div className={styles.primarySubscriberTitle}>
              {primarySubscriberTitle}
            </div>
            <InfoSection entries={getPrimarySubscriberMainData(insurance)} />
            <InfoSection entries={getPrimarySubscriberAddressData(insurance)} />
          </Fragment>
        )}

        <UserDocumentsPreview documents={filteredDocuments} />
      </Fragment>
    );
  },
);

InsuranceSection.propTypes = {
  flowType: PropTypes.string,
  isAdditionalInsurance: PropTypes.bool,
  insurance: PropTypes.object,
  additionalInsuranceKey: PropTypes.string,
  skipInsurance: PropTypes.bool,
};

const InsurancesInfo = ({
  flowType,
  toDoTaskData,
  userId,
  userName,
  withSelfPayEnding,
}) => {
  const { formatMessage } = useIntl();

  const [openIds, setOpenIds] = useState([]);

  const additionalInsurances = useSelector(selectPatientInsurancesList);

  const error = useSelector(selectInsuranceLatestCheckError);

  const isPlanNotListedSelected = useSelector(
    selectPaperworkIsPlanNotListedSelected,
  );

  const errorCode = get(error, 'code');

  const notFoundErrorCode = errorCode === ERROR_CODES.RECORD_NOT_FOUND;

  const patientInsurance = useGetPatientMainInsuranceInfo({
    flowType,
    toDoTaskData,
  });

  const {
    isAdditionalInsurancesEnabled,
  } = useIsAdditionalInsurancesEnabledAndMaxNumber();

  const isPaperworkFlowType = checkIsPaperworkFlow(flowType);

  const { skipInsurance } = patientInsurance;

  const showNoInsuranceTitle =
    !skipInsurance && notFoundErrorCode && !isPaperworkFlowType;

  const showInsuranceInfo =
    (!skipInsurance && !notFoundErrorCode) ||
    (isPaperworkFlowType && isPlanNotListedSelected);

  const showSkipInsuranceTitle = skipInsurance && !isPlanNotListedSelected;

  const showAdditionalInsuranceSection =
    !skipInsurance && isAdditionalInsurancesEnabled;

  const haveInsuranceCheckboxLabel = useGetHaveInsuranceLabel({
    formatMessage,
    targetUserName: userName,
    targetUserId: userId,
    withSelfPayEnding,
  });

  const additionalInsuranceTitle = formatMessage({
    id: 'profile.insurance.additional',
  });

  const noInsuranceTitle = formatMessage({
    id: 'profile.section.insurance.empty',
  });

  const hasAdditionalInsurances = Boolean(additionalInsurances.length);

  const toggleSection = id => {
    setOpenIds(prev =>
      prev.includes(id) ? prev.filter(openId => openId !== id) : [...prev, id],
    );
  };

  return (
    <section className={styles.section}>
      {showNoInsuranceTitle && (
        <div className={styles.title}>{noInsuranceTitle}</div>
      )}

      {showSkipInsuranceTitle && (
        <div className={styles.title}>{haveInsuranceCheckboxLabel}</div>
      )}

      {showInsuranceInfo && (
        <Fragment>
          <InsuranceSection
            insurance={patientInsurance}
            flowType={flowType}
            isAdditionalInsurance={false}
            skipInsurance={skipInsurance || isPlanNotListedSelected}
          />

          {showAdditionalInsuranceSection && (
            <Fragment>
              <div className={styles.additionalInsurancesTitle}>
                {additionalInsuranceTitle}
              </div>

              {!hasAdditionalInsurances && (
                <div className={styles.withoutAdditionalInsurances}>
                  {DEFAULT_EMPTY_VALUE}
                </div>
              )}

              {additionalInsurances.map(insurance => {
                const isOpen = openIds.includes(insurance.id);

                return (
                  <Fragment key={insurance.id}>
                    <button
                      type="button"
                      className={styles.toggleBtn}
                      onClick={() => toggleSection(insurance.id)}
                      aria-expanded={isOpen}
                      aria-controls={`insurance-section-${insurance.id}`}
                    >
                      {additionalInsuranceTitle}
                      <Icon
                        size={12}
                        name="arrow-down-forms"
                        className={cn({ [styles.icon]: isOpen })}
                        style={ICON_STYLES}
                      />
                    </button>
                    {isOpen && (
                      <InsuranceSection
                        insurance={insurance}
                        flowType={flowType}
                        isAdditionalInsurance
                        additionalInsuranceKey={`insurance-section-${insurance.id}`}
                      />
                    )}
                    <div className={styles.divider} />
                  </Fragment>
                );
              })}
            </Fragment>
          )}
        </Fragment>
      )}
    </section>
  );
};

InsurancesInfo.propTypes = {
  flowType: PropTypes.string.isRequired,
  toDoTaskData: PropTypes.object,
  userId: PropTypes.number,
  userName: PropTypes.string,
  withSelfPayEnding: PropTypes.bool,
};

export default memo(InsurancesInfo);



// WEBPACK FOOTER //
// ./src/components/InsurancesInfo/index.jsx